import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, Loader2, TriangleAlert } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { doTimeSlotsOverlap, filterSlotsWithoutClosure, getOverlappingSlotIds, type ClosureRange } from "@/lib/timeSlotClosureUtils";

interface TimeSlotOption {
  id: string;
  label: string;
  startTime: string;
  endTime: string;
}

interface TimeSlotClosureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (timeSlotIds: string[], startDate: string, endDate: string, reason?: string, customerMessage?: string) => Promise<void>;
  initialData?: {
    timeSlotId: string;
    startDate: string;
    endDate: string;
    reason?: string;
    customerMessage?: string;
  };
  mode: 'create' | 'edit';
  timeSlots: TimeSlotOption[];
  existingClosures?: ClosureRange[];
  /** Pre-select a specific time slot (used when clicking "Bloquer" on a slot card) */
  preselectedSlotId?: string;
}

export function TimeSlotClosureDialog({
  open,
  onOpenChange,
  onSubmit,
  initialData,
  mode,
  timeSlots,
  existingClosures = [],
  preselectedSlotId,
}: TimeSlotClosureDialogProps) {
  const [timeSlotId, setTimeSlotId] = useState("");
  const [selectionMode, setSelectionMode] = useState<'single' | 'service'>('single');
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [reason, setReason] = useState("");
  const [customerMessage, setCustomerMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  useEffect(() => {
    if (open && initialData) {
      setTimeSlotId(initialData.timeSlotId);
      setStartDate(new Date(initialData.startDate));
      setEndDate(new Date(initialData.endDate));
      setReason(initialData.reason || "");
      setCustomerMessage(initialData.customerMessage || "");
      setSelectionMode('single');
    } else if (open) {
      setTimeSlotId(preselectedSlotId || "");
      setStartDate(undefined);
      setEndDate(undefined);
      setReason("");
      setCustomerMessage("");
      setSelectionMode('single');
    }
  }, [open, initialData, preselectedSlotId]);

  const handleSubmit = async () => {
    if (!timeSlotId || !startDate || !endDate) return;

    setLoading(true);
    try {
      await onSubmit(
        selectedSlotIds,
        format(startDate, 'yyyy-MM-dd'),
        format(endDate, 'yyyy-MM-dd'),
        reason.trim() || undefined,
        customerMessage.trim() || undefined
      );
      onOpenChange(false);
    } catch (error) {
      // Error handled in hook
    } finally {
      setLoading(false);
    }
  };

  const selectedSlot = timeSlots.find(slot => slot.id === timeSlotId);
  const overlappingSlots = selectedSlot
    ? timeSlots.filter(slot =>
        slot.id !== selectedSlot.id &&
        doTimeSlotsOverlap(slot, selectedSlot)
      )
    : [];
  const selectedSlotIds = selectionMode === 'service' && selectedSlot
    ? getOverlappingSlotIds(selectedSlot, timeSlots)
    : timeSlotId ? [timeSlotId] : [];
  const formattedStartDate = startDate ? format(startDate, 'yyyy-MM-dd') : null;
  const formattedEndDate = endDate ? format(endDate, 'yyyy-MM-dd') : null;
  const creatableSlotIds = formattedStartDate && formattedEndDate
    ? filterSlotsWithoutClosure(selectedSlotIds, existingClosures, formattedStartDate, formattedEndDate)
    : selectedSlotIds;
  const skippedSlotCount = selectedSlotIds.length - creatableSlotIds.length;

  const isValid = timeSlotId && startDate && endDate && endDate >= startDate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Bloquer un créneau' : 'Modifier le blocage'}
          </DialogTitle>
          <DialogDescription>
            Choisissez un seul créneau ou bloquez tout le service couvert par cette plage.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Time Slot Selector */}
          <div className="grid gap-2">
            <Label>Créneau concerné</Label>
            <Select value={timeSlotId} onValueChange={setTimeSlotId}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner un créneau" />
              </SelectTrigger>
              <SelectContent>
                {timeSlots.map(ts => (
                  <SelectItem key={ts.id} value={ts.id}>
                    {ts.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {mode === 'create' && selectedSlot && (
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="Étendue du blocage">
              <Button
                type="button"
                variant={selectionMode === 'single' ? 'default' : 'outline'}
                onClick={() => setSelectionMode('single')}
              >
                Ce créneau
              </Button>
              <Button
                type="button"
                variant={selectionMode === 'service' ? 'default' : 'outline'}
                onClick={() => setSelectionMode('service')}
              >
                Tout le service
              </Button>
            </div>
          )}

          {mode === 'create' && selectionMode === 'single' && overlappingSlots.length > 0 && (
            <div className="flex gap-3 rounded-md border border-border bg-muted/50 p-3 text-sm">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <div>
                <p className="font-medium">{overlappingSlots.length} autres créneaux resteront ouverts.</p>
                <p className="mt-1 text-muted-foreground">
                  Départs concernés : {overlappingSlots.map(slot => slot.startTime).join(', ')}.
                </p>
                <Button type="button" variant="link" className="h-auto p-0 mt-1" onClick={() => setSelectionMode('service')}>
                  Les bloquer aussi
                </Button>
              </div>
            </div>
          )}

          {mode === 'create' && selectionMode === 'service' && selectedSlotIds.length > 0 && (
            <p className="text-sm text-muted-foreground">
              {creatableSlotIds.length} nouveau{creatableSlotIds.length > 1 ? 'x' : ''} créneau{creatableSlotIds.length > 1 ? 'x' : ''} sera{creatableSlotIds.length > 1 ? 'ont' : ''} bloqué{creatableSlotIds.length > 1 ? 's' : ''} en une seule action.
              {skippedSlotCount > 0 && ` ${skippedSlotCount} est déjà bloqué${skippedSlotCount > 1 ? 's' : ''} sur cette période.`}
            </p>
          )}

          {/* Start Date */}
          <div className="grid gap-2">
            <Label>Date de début</Label>
            <Popover open={startOpen} onOpenChange={setStartOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !startDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate ? format(startDate, "PPP", { locale: fr }) : "Sélectionner une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={(date) => {
                    setStartDate(date);
                    setStartOpen(false);
                    if (date && endDate && endDate < date) {
                      setEndDate(undefined);
                    }
                  }}
                  initialFocus
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* End Date */}
          <div className="grid gap-2">
            <Label>Date de fin</Label>
            <Popover open={endOpen} onOpenChange={setEndOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !endDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {endDate ? format(endDate, "PPP", { locale: fr }) : "Sélectionner une date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={(date) => {
                    setEndDate(date);
                    setEndOpen(false);
                  }}
                  disabled={(date) => startDate ? date < startDate : false}
                  initialFocus
                  locale={fr}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Reason */}
          <div className="grid gap-2">
            <Label>Raison interne (optionnel)</Label>
            <Input
              placeholder="Ex: Événement privé, maintenance..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Visible uniquement par le staff</p>
          </div>

          {/* Customer Message */}
          <div className="grid gap-2">
            <Label>Message affiché au client (optionnel)</Label>
            <Textarea
              placeholder="Ex: Fermé pour événement privé"
              value={customerMessage}
              onChange={(e) => setCustomerMessage(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Si vide, le message par défaut sera : « Établissement fermé ce jour »
            </p>
          </div>

          {startDate && endDate && endDate < startDate && (
            <p className="text-sm text-destructive">
              La date de fin doit être postérieure ou égale à la date de début
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || loading}>
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Bloquer' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
